// The Chain of a Case in three parts: now (the current steps), later (open
// steps waiting for others) and done (closed). Closed steps are not greyed
// out (the check says they are closed); results are read in the opened step.
//
// Promise:
// - Pressing a step's title (or the empty part of its row) only opens or
//   closes it in place: folded, the title is one line; open, it wraps and
//   shows the body, a thin line, the result and when it was made. A folded
//   step of Later is its title alone (no dates, check or "…").
// - The check (at the right end) closes the step or opens it again. Closing an
//   open step folds it; the focus stays on the check, also when the step moves
//   to another part. A refused write puts the step back as it was.
// - Renaming is in the "…" of the step (with the day to do it, the due date,
//   a link to the Case and delete, which asks first and names the step).
//   Other parts add rows at its top through the host (`CasesExtensions`).
// - What other parts mark on a step (row marks of kind "step") leads the line
//   under its title, and, opened, its button stands before the "…".
// - An opening asked from the list brings the step's title to the top; a step
//   just added opens with the caret in its body.
// - A step added on the screen without a place of its own is marked
//   (`unplaced`) until AI places it.
import {
  CASE_TEXT_MAX,
  CASE_TITLE_MAX,
  type CaseDetail,
  type CaseLink,
  type CaseStep,
  orderSteps,
} from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { isOptimisticId } from "../data/optimistic";
import { useFormat, useTimeZone } from "../me/profile";
import { RefTextField } from "../refs/ref-text-field";
import { copyLinkItem } from "../shell/copy-link";
import { RowMarks, useRowNotes } from "../shell/header-actions";
import { usePhone } from "../shell/phone";
import { ActionCheck } from "../ui/action-check";
import { confirmAction } from "../ui/confirm";
import { DatePopover } from "../ui/date-popover";
import { Caret, Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { MenuButton } from "../ui/menu";
import { Popover } from "../ui/popover";
import { Tooltip } from "../ui/tooltip";
import { DayPopover, SECTION, TargetName } from "./bits";
import {
  dateIn,
  endOfDay,
  type PlanPreset,
  planLabel,
  planOfPreset,
  presetOf,
  todayIn,
} from "./dates";
import { useStepMenuHook } from "./extensions";
import { CaseFiles } from "./files";
import type { CasePatch, StepUpdateInput } from "./queries";
import type { Opened } from "./screen-state";

export function chainParts(steps: readonly CaseStep[]) {
  const ordered = orderSteps(steps);
  return {
    now: ordered.filter((s) => s.status === "open" && s.current),
    later: ordered.filter((s) => s.status === "open" && !s.current),
    done: ordered.filter((s) => s.status !== "open"),
  };
}

export interface ChainProps {
  c: CaseDetail;
  /** What is open in place, and how it was asked for. */
  opened: Opened | null;
  onOpen(stepId: string | null): void;
  /** The opened step was brought into view (do it once). */
  onRevealed(): void;
  /** Writes a change of a step; the caller keeps a refusal for that step's row. */
  write(patch: CasePatch, lead: string, stepId: string): void;
  /** The same write, awaited (an extension's row of the step's "…"). */
  writeAsync(
    input: Readonly<Record<string, unknown>>,
    lead: string,
    stepId: string,
  ): Promise<unknown>;
  /** The refusal to show right under a step's row, if any. */
  stepError(stepId: string): ReactNode;
  /** Saves one text of a step (body, result); rejects with the server's error. */
  saveText(
    stepId: string,
    field: "body" | "result",
    text: string,
  ): Promise<unknown>;
}

export function Chain(props: ChainProps) {
  const { c, opened } = props;
  // The step whose check keeps the focus: closing or reopening moves the step
  // to another part, where its row is made again.
  const [keepCheck, setKeepCheck] = useState<string | null>(null);
  // On a phone Later is folded under its heading with a count (the old web's):
  // the screen is short, and what waits is read when wanted. A step opened
  // from elsewhere (a list's row, a link) opens it.
  const phone = usePhone();
  const [laterOpen, setLaterOpen] = useState(false);
  if (c.steps.length === 0)
    return <p className="text-[12.5px] text-faint">{m.cases_chain_empty()}</p>;
  const { now, later, done } = chainParts(c.steps);
  const openId = opened?.stepId ?? null;
  const laterShown = !phone || laterOpen || later.some((s) => s.id === openId);
  const row = (s: CaseStep, later = false) => (
    <StepItem
      key={s.id}
      {...props}
      step={s}
      later={later}
      expanded={openId === s.id && !isOptimisticId(s.id)}
      focusBody={openId === s.id && opened?.focus === "body"}
      reveal={openId === s.id && opened?.reveal === true}
      keepCheck={keepCheck === s.id}
      onCheckKept={() => setKeepCheck(null)}
      onChecked={(closing) => {
        setKeepCheck(s.id);
        if (closing && openId === s.id) props.onOpen(null);
      }}
      onToggle={() => {
        setKeepCheck(null);
        props.onOpen(openId === s.id ? null : s.id);
      }}
    />
  );
  return (
    <div className="flex flex-col">
      <ChainPart name={m.cases_chain_now()}>{now.map((s) => row(s))}</ChainPart>
      <ChainPart
        name={m.cases_chain_later()}
        fold={
          phone
            ? {
                open: laterShown,
                count: later.length,
                onToggle: () => setLaterOpen(!laterShown),
              }
            : undefined
        }
      >
        {later.map((s) => row(s, true))}
      </ChainPart>
      <ChainPart name={m.cases_chain_done()}>
        {done.map((s) => row(s))}
      </ChainPart>
    </div>
  );
}

function ChainPart({
  name,
  fold,
  children,
}: {
  name: string;
  /** Folded under its heading (the heading opens it and says how many). */
  fold?: { open: boolean; count: number; onToggle(): void };
  children: ReactNode[];
}) {
  if (children.length === 0) return null;
  return (
    <section aria-label={name} data-chain-part="">
      {fold ? (
        <button
          type="button"
          aria-expanded={fold.open}
          onClick={fold.onToggle}
          className={`${SECTION} mt-4 flex cursor-pointer items-center gap-1`}
        >
          {name} <span className="tabular-nums">{fold.count}</span>
          <Caret open={fold.open} />
        </button>
      ) : (
        <div className={`${SECTION} mt-4`}>{name}</div>
      )}
      {(!fold || fold.open) && (
        <ol className="flex flex-col gap-[6px]">{children}</ol>
      )}
    </section>
  );
}

function StepItem({
  c,
  step,
  later,
  expanded,
  focusBody,
  reveal,
  keepCheck,
  onCheckKept,
  onChecked,
  onRevealed,
  write,
  writeAsync,
  stepError,
  saveText,
  onToggle,
}: ChainProps & {
  step: CaseStep;
  /** A row of Later: folded, it is the title alone (no dates, check or "…"). */
  later: boolean;
  expanded: boolean;
  /** Open the body for writing at once (a step just added). */
  focusBody: boolean;
  /** Bring the title to the top of the detail once (opened from the list). */
  reveal: boolean;
  /** Give the check the focus once (it was pressed in the row this one replaced). */
  keepCheck: boolean;
  onCheckKept(): void;
  /** The check was pressed: closing (true) or reopening. */
  onChecked(closing: boolean): void;
  onToggle(): void;
}) {
  const format = useFormat();
  const timeZone = useTimeZone();
  const regionId = useId();
  const menuAnchor = useRef<HTMLSpanElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const checkRef = useRef<HTMLInputElement>(null);
  // frontend-development: not a fetch; the check pressed keeps the focus when its row is made again in another part.
  useEffect(() => {
    if (keepCheck && document.activeElement !== checkRef.current)
      checkRef.current?.focus({ preventScroll: true });
  }, [keepCheck]);
  const [panel, setPanel] = useState<"plan" | "due" | null>(null);
  const [renaming, setRenaming] = useState(false);
  const open = step.status === "open";
  const pending = isOptimisticId(step.id);
  // A folded row of Later is its title alone: what waits on others is read,
  // not acted on, until it opens (the old web's rule for Later).
  const folded = later && !expanded;
  const useStepMenu = useStepMenuHook();
  const extraItems = useStepMenu({
    caseId: c.id,
    step,
    write: (input, lead) => writeAsync(input, lead, step.id),
  });
  // frontend-development: not a fetch; brings the step opened from the list to the top once.
  useEffect(() => {
    if (!expanded || !reveal) return;
    rowRef.current?.scrollIntoView?.({ block: "start" });
    onRevealed();
  }, [expanded, reveal, onRevealed]);
  const waiting = step.kind === "waiting";
  const today = todayIn(timeZone);
  const plan = planLabel(step, today);
  const date = waiting ? step.waitUntil : step.dueAt;
  const update = (patch: Omit<StepUpdateInput, "id">, lead: string) =>
    write({ update_steps: [{ ...patch, id: step.id }] }, lead, step.id);

  // What other parts tell of the step lead its line (the Agent chat part:
  // what the chat it was handed to does).
  const notes = useRowNotes("step", {
    targetId: step.id,
    ext: step.ext,
    closed: !open,
  });
  const meta: ReactNode[] = notes.map(({ id, node }) => (
    <span key={`note:${id}`}>{node}</span>
  ));
  if (waiting && step.waitTarget)
    meta.push(
      <TargetName
        key="target"
        target={step.waitTarget}
        className="text-body-soft"
        asWait
      />,
    );
  if (waiting && open && step.waitTargetDroppedAt)
    meta.push(
      <span key="dropped" className="text-danger">
        {m.cases_step_wait_target_dropped()}
      </span>,
    );
  if (waiting && !open && step.autoClosedAt)
    meta.push(<span key="auto">{m.cases_step_auto_closed()}</span>);
  if (plan)
    meta.push(<span key="plan">{m.cases_step_plan({ label: plan })}</span>);
  // A due date past stays grey here: the warm color is kept for a wait whose
  // target was dropped (and for the list's mark), where the person must act.
  if (date)
    meta.push(
      <span key="due">
        {m.cases_step_due({ date: format.relativeDay(date) })}
      </span>,
    );
  if (step.unplaced && open)
    meta.push(
      <Tooltip key="unplaced" text={m.cases_step_unplaced_hint()}>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focusable so the hint can be read from the keyboard */}
        <span tabIndex={0} data-unplaced="" className="text-warn">
          {m.cases_step_unplaced()}
        </span>
      </Tooltip>,
    );
  // Opened, the day it was made joins the line under the title, with the
  // other dates (the old web's: the dates read together, not under the body).
  if (expanded)
    meta.push(
      <span key="created" className="tabular-nums">
        {m.cases_step_created({ date: format.day(step.createdAt) })}
      </span>,
    );

  const toggleDone = () => {
    onChecked(open);
    if (open)
      update(
        { status: "done" },
        m.cases_step_close_failed({ title: step.title }),
      );
    else
      update(
        { status: "open" },
        m.cases_step_reopen_failed({ title: step.title }),
      );
  };

  return (
    <li data-step-id={step.id}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: a shortcut for the empty part of the row; the keyboard uses the title's button */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: same as above */}
      <div
        ref={rowRef}
        onClick={(e) => {
          if (pending || renaming) return;
          if (
            (e.target as Element).closest(
              "input, button, a, [role=dialog], [role=menu]",
            )
          )
            return;
          onToggle();
        }}
        // The highlight covers the whole row, the "…" and the check included,
        // and stays while the step's menu is open.
        className={`group/step -ml-[6px] flex scroll-mt-4 items-start gap-[8px] rounded-[6px] pr-[2px] pl-[6px] ${
          pending
            ? ""
            : "cursor-pointer hover:bg-hover has-[[aria-haspopup][aria-expanded=true]]:bg-hover"
        }`}
      >
        {renaming ? (
          <span className="flex min-w-0 flex-1 items-center gap-[8px] py-[2px]">
            <StepIcon step={step} />
            <input
              // biome-ignore lint/a11y/noAutofocus: "rename" was chosen to write here
              autoFocus
              defaultValue={step.title}
              maxLength={CASE_TITLE_MAX}
              aria-label={m.cases_step_title()}
              onKeyDown={(e) => {
                if (isImeComposing(e.nativeEvent)) return;
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.currentTarget.value = step.title;
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                const next = e.currentTarget.value.trim();
                setRenaming(false);
                if (next && next !== step.title)
                  update(
                    { title: next },
                    m.cases_step_rename_failed({ title: step.title }),
                  );
              }}
              className="plain-field min-w-0 flex-1 border-none bg-transparent p-0 text-[14.5px] leading-[22px] text-fg outline-none"
            />
          </span>
        ) : (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? regionId : undefined}
            disabled={pending}
            onClick={onToggle}
            className="flex min-w-0 flex-1 cursor-pointer items-start gap-[8px] py-[2px] text-left text-[14.5px] leading-[22px] disabled:cursor-default"
          >
            <StepIcon step={step} />
            <span
              data-step-title=""
              className={`min-w-0 text-fg ${expanded ? "break-words" : "truncate"}`}
            >
              {step.title}
            </span>
            <span className="flex h-[22px] flex-none items-center text-[12px] text-faint">
              <Caret open={expanded} />
            </span>
          </button>
        )}
        {!pending && !folded && (
          <span className="ml-auto flex flex-none gap-[8px]">
            {/* Opened, the way to what other parts link to the step (the
                Agent chat part: the chat it was handed to), before the "…". */}
            {expanded && (
              <RowMarks
                kind="step"
                place="button"
                targetId={step.id}
                ext={step.ext}
                round
              />
            )}
            {/* The "…" shows on hover or focus of its row (the keyboard still
                reaches it); while hidden it cannot be pressed. On touch it is
                there only on the opened row, so a tap on a closed row opens
                the row instead of landing on an unseen button. */}
            <span
              ref={menuAnchor}
              className={`flex flex-none opacity-0 pointer-events-none group-hover/step:opacity-100 group-hover/step:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto has-[[aria-expanded=true]]:opacity-100 has-[[aria-expanded=true]]:pointer-events-auto ${
                expanded
                  ? "pointer-coarse:opacity-100 pointer-coarse:pointer-events-auto"
                  : "pointer-coarse:hidden"
              } ${panel ? "opacity-100 pointer-events-auto" : ""}`}
            >
              <MenuButton
                label={m.cases_step_menu({ title: step.title })}
                align="end"
                className="inline-flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[13px] text-muted hover:bg-selected hover:text-body"
                items={[
                  ...extraItems,
                  {
                    id: "rename",
                    label: m.cases_rename(),
                    onSelect: () => setRenaming(true),
                  },
                  ...(waiting || !open
                    ? []
                    : [
                        {
                          id: "plan",
                          label: m.cases_change_plan(),
                          onSelect: () => setPanel("plan"),
                        },
                      ]),
                  {
                    id: "due",
                    label: waiting
                      ? m.cases_change_wait_until()
                      : m.cases_change_due(),
                    onSelect: () => setPanel("due"),
                  },
                  // The Case's own path: which step is open in place is screen
                  // state, not part of the URL, so the link opens the Case.
                  copyLinkItem(`/me/cases/${c.id}`),
                  "separator",
                  {
                    id: "delete",
                    label: m.common_delete(),
                    danger: true,
                    onSelect: async () => {
                      const ok = await confirmAction({
                        title: m.cases_step_delete_title({ title: step.title }),
                        body: m.cases_step_delete_body(),
                        action: m.common_delete(),
                        danger: true,
                      });
                      if (!ok) return;
                      write(
                        { delete_steps: [step.id] },
                        m.cases_step_delete_failed({ title: step.title }),
                        step.id,
                      );
                    },
                  },
                ]}
              >
                <Icon kind="more" size={14} />
              </MenuButton>
              <PlanPopover
                anchor={menuAnchor}
                open={panel === "plan"}
                onClose={() => setPanel(null)}
                step={step}
                today={today}
                timeZone={timeZone}
                onChange={(plan) =>
                  update(
                    { plan },
                    m.cases_step_plan_failed({ title: step.title }),
                  )
                }
              />
              <DayPopover
                anchor={menuAnchor}
                open={panel === "due"}
                onClose={() => setPanel(null)}
                label={waiting ? m.cases_wait_until() : m.cases_due()}
                value={date ? dateIn(date, timeZone) : ""}
                clearLabel={m.cases_due_clear()}
                onChange={(d) => {
                  const at = d ? endOfDay(d, timeZone) : null;
                  update(
                    waiting ? { wait_until: at } : { due_at: at },
                    m.cases_step_due_failed({ title: step.title }),
                  );
                }}
              />
            </span>
          </span>
        )}
        {!folded && (
          <ActionCheck
            aria-label={
              waiting
                ? m.cases_step_resolve({ title: step.title })
                : m.cases_step_done({ title: step.title })
            }
            checked={!open}
            disabled={pending}
            onChange={toggleDone}
            ref={checkRef}
            // Moving on to something else ends keeping the focus here.
            onBlur={(e) => {
              if (keepCheck && e.relatedTarget) onCheckKept();
            }}
            className={`${pending ? "ml-auto " : ""}mt-[5px]`}
          />
        )}
      </div>
      {meta.length > 0 && !folded && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-[6px] pl-[26px] text-[12px] text-faint">
          {meta.map((node, i) => (
            <Fragment key={(node as ReactElement).key}>
              {i > 0 && <span aria-hidden="true">·</span>}
              {node}
            </Fragment>
          ))}
        </div>
      )}
      <div className="pl-[26px]">{stepError(step.id)}</div>
      {expanded && (
        <section
          id={regionId}
          aria-label={step.title}
          // A line down from the middle of the 18px icon (8.5 + 1px) that
          // holds the body and the result; they start at the title's left
          // (8.5 + 1 + 16.5 = 26px).
          className="mt-[6px] mb-[8px] ml-[8.5px] flex flex-col gap-[10px] border-l border-tree pl-[16.5px]"
        >
          <div data-step-slot="body">
            <RefTextField
              // Made again for a step just added, so the caret goes in at once.
              key={focusBody ? "just-added" : "read"}
              value={step.body}
              onSave={(text) => saveText(step.id, "body", text)}
              label={m.cases_body()}
              placeholder={m.cases_body_placeholder()}
              maxLength={CASE_TEXT_MAX}
              autoEdit={focusBody}
              textClass="text-[13.5px] leading-[1.7] text-body"
            />
            <CaseFiles caseId={c.id} files={step.files.body} />
            <StepLinks links={step.links.body} />
          </div>
          <div
            data-step-slot="result"
            className="border-t border-border-soft pt-[10px]"
          >
            <RefTextField
              value={step.result ?? ""}
              onSave={(text) => saveText(step.id, "result", text)}
              label={m.cases_result()}
              placeholder={m.cases_result_placeholder()}
              maxLength={CASE_TEXT_MAX}
              textClass="text-[13.5px] leading-[1.7] text-body"
            />
            <CaseFiles caseId={c.id} files={step.files.result} />
            <StepLinks links={step.links.result} />
          </div>
        </section>
      )}
    </li>
  );
}

function StepIcon({ step }: { step: CaseStep }) {
  return (
    // A size up from the list's 16px, beside the 14.5px title; with the 8px
    // gap the title starts at 26px, where the lines under it start too.
    <span className="flex h-[22px] flex-none items-center text-faint">
      <Icon kind={step.kind === "waiting" ? "waiting" : "action"} size={18} />
    </span>
  );
}

/** The links written to a step's body or result, by their current names. */
function StepLinks({ links }: { links: readonly CaseLink[] }) {
  if (links.length === 0) return null;
  return (
    <ul className="mt-[6px] flex flex-wrap gap-[6px]">
      {links.map((l) => (
        <li
          key={l.id}
          className="inline-flex min-w-0 max-w-full items-center rounded-[5px] bg-hover px-[6px] py-[2px] text-[12px] text-body"
        >
          {l.kind === "url" ? (
            <a
              href={l.targetId}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 truncate hover:underline"
            >
              {l.label || l.targetId}
            </a>
          ) : (
            <TargetName target={l.target} />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The day to do an Action: today, tomorrow, this week, later, a day, or none.
 * TimeBlocker's cards use it too (the same panel as a step's "…").
 */
export function PlanPopover({
  anchor,
  open,
  onClose,
  step,
  today,
  timeZone,
  onChange,
  align = "end",
  heading,
  minimal = false,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose(): void;
  /** Which edge of the anchor the panel lines up with (a step's "…": its end). */
  align?: "start" | "end";
  /** A line over the choices that says what the menu changes (where it is not clear from its button). */
  heading?: string;
  /** Only "pick a date" and "no day" (where a day is not for choosing among presets). */
  minimal?: boolean;
  step: Pick<CaseStep, "plan" | "someday">;
  today: string;
  /** The account's time zone ("today" in the calendar). */
  timeZone: string | undefined;
  onChange(
    plan: { scope: "day" | "week"; on: string } | "someday" | null,
  ): void;
}) {
  const [picking, setPicking] = useState(false);
  const current = presetOf(step, today);
  const presets: { key: PlanPreset; label: string }[] = [
    { key: "today", label: m.cases_plan_today() },
    { key: "tomorrow", label: m.cases_plan_tomorrow() },
    { key: "week", label: m.cases_plan_this_week() },
    { key: "someday", label: m.cases_plan_someday() },
  ];
  const choose = (plan: Parameters<typeof onChange>[0]) => {
    onChange(plan);
    setPicking(false);
    onClose();
  };
  const row = (on: boolean) =>
    `flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] hover:bg-hover focus:bg-hover focus:outline-none ${
      on ? "text-accent-deep" : "text-body"
    }`;
  const close = () => {
    setPicking(false);
    onClose();
  };
  return (
    <>
      {/* "Pick a date…" opens the calendar from the same place. */}
      <DatePopover
        anchor={anchor}
        open={open && picking}
        onClose={close}
        label={m.cases_plan()}
        align={align}
        value={step.plan?.scope === "day" ? step.plan.on : ""}
        clearLabel={m.cases_plan_clear()}
        timeZone={timeZone}
        onChange={(day) => choose(day ? { scope: "day", on: day } : null)}
      />
      <Popover
        anchor={anchor}
        open={open && !picking}
        onClose={close}
        label={m.cases_change_plan()}
        align={align}
      >
        <div role="menu" aria-label={m.cases_change_plan()}>
          {heading && (
            <div
              aria-hidden="true"
              className="px-3 pt-2 pb-1 text-[11.5px] text-faint"
            >
              {heading}
            </div>
          )}
          {(minimal ? [] : presets).map((p) => (
            <button
              key={p.key}
              type="button"
              role="menuitemradio"
              aria-checked={current === p.key}
              className={row(current === p.key)}
              onClick={() => {
                if (current !== p.key) choose(planOfPreset(p.key, today));
                else onClose();
              }}
            >
              <span className="w-4 flex-none">
                {current === p.key && <Icon kind="check" size={14} />}
              </span>
              {p.label}
            </button>
          ))}
          {!minimal && <hr className="my-1 h-px border-0 bg-border-soft" />}
          <button
            type="button"
            role="menuitemradio"
            aria-checked={current === "date"}
            className={row(current === "date")}
            onClick={() => setPicking(true)}
          >
            <span className="w-4 flex-none">
              {current === "date" && <Icon kind="check" size={14} />}
            </span>
            {m.cases_plan_pick_date()}
          </button>
          {current !== null && (
            <button
              type="button"
              role="menuitem"
              className={row(false)}
              onClick={() => choose(null)}
            >
              <span className="w-4 flex-none" />
              {m.cases_plan_clear()}
            </button>
          )}
        </div>
      </Popover>
    </>
  );
}
